import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import SearchBox from "@atoms/SearchBox";
import { CustomerService, HEADPHONE, WRENCH } from "@config/images";
import { fontFamSecondary } from "@definitions/mui/custom";
import Text from "@ds/components/Text";
import Button from "@ds/components/Button";
import DialogForm from "@ds/components/FormDialog";
import useResponsive from "@helpers/useResponsive";
import DynamicImage from "@ds/components/DynamicImage";
import { ChevronRightIcon } from "@icons/mobile";
import useAction from "./hooks/useAction";
import type { FaqLayoutProps, IFilter, ISupporHelpdesk } from "./interface";
import FilterFaq from "./lib/FilterFaq/FilterFaq";
import ListFaq from "./lib/ListFaq/ListFaq";
import useStyles from "./styles";

const FAQ: React.FC<FaqLayoutProps> = ({
  isGetSupport,
  dataFaq,
  searchIconShown = false /** temporary comment because rendering double search box */,
}) => {
  const { t } = useTranslation("faq");
  const router = useRouter();
  const {
    handleSearch,
    helpdeskData,
    apiWA,
    search,
    mdClient,
    isModalHelpdesk,
    setIsModalHelpdesk,
    filterData,
    getDetailHelpDesk,
    isAuth,
  } = useAction(t);

  const isMobile = useResponsive("md");
  const filterProps = {
    search: {
      onChange: handleSearch,
    },
  };
  const isHaveTopic = filterData?.find(
    (el: IFilter) => el.path === router?.pathname?.split("/")[2],
  )?.title;
  const isMD = useResponsive("md");
  const classes = useStyles();
  const dataStaticHelpdesk = [
    {
      icon: HEADPHONE,
      title: t("helpdesk.0.title"),
      desc: t("helpdesk.0.desc"),
      action: () => {
        setIsModalHelpdesk(true);
        getDetailHelpDesk("6281119678999");
      },
    },
    {
      icon: WRENCH,
      title: t("helpdesk.1.title"),
      desc: t("helpdesk.1.desc"),
      action: () => {
        if (isAuth) {
          router.push("/new-ticket");
        } else {
          router.push("/login");
        }
      },
    },
  ];

  const renderTopicTitle = () => {
    if (!search && !isHaveTopic) {
      return (
        <Box mb={3} mx={1} mt={3}>
          <Text
            variant={mdClient ? "body1" : "h2"}
            weight="700"
            sx={{
              fontSize: mdClient ? "16px" : "20px",
            }}
          >
            {t("topics.subtitle")}
          </Text>
        </Box>
      );
    }

    return (
      <Box mb={3} mx={1} mt={3}>
        <Text variant="h2" weight="700" sx={{ fontSize: "20px" }}>
          {isHaveTopic}
        </Text>
      </Box>
    );
  };

  const renderHelpdeskContact = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      {dataStaticHelpdesk.map((item: ISupporHelpdesk, idx: number) => (
        <Box
          key={idx}
          borderRadius={isMD ? "8px" : "12px"}
          border="1px solid #DEE3ED"
          color="#2E434D"
          position="relative"
          onClick={item.action}
          sx={{
            cursor: "pointer",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mx={isMD ? "12px" : undefined}
          >
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              gap={2}
              my={isMD ? "12px" : 2}
              ml={isMD ? 0 : 2}
            >
              <Box component="img" src={item.icon} alt="dummy-gambar" />
              <Box display="flex" flexDirection="column">
                <Text
                  variant={isMD ? "body2" : "body1"}
                  weight="700"
                  sx={{ lineHeight: isMD ? "19px" : undefined }}
                >
                  {item.title}
                </Text>
                <Text
                  variant={isMD ? "caption1" : "body2"}
                  weight={isMD ? "400" : undefined}
                  sx={{
                    ...(isMD && {
                      marginTop: "4px",
                      lineHeight: "16px",
                    }),
                  }}
                >
                  {item.desc}
                </Text>
              </Box>
            </Box>
            {isMD && <ChevronRightIcon />}
          </Box>
        </Box>
      ))}
    </Box>
  );

  const renderListFaqItem = () => {
    return <ListFaq dataFaqList={dataFaq} search={search} />;
  };

  const renderHelpdeskLayout = (isGetSupport: boolean) => (
    <Box mx={isMD ? 0 : 1} mt={isMD ? 0 : 3}>
      <Box mb="16px">
        <Text
          variant={mdClient ? "subtitle1" : "h2"}
          weight="700"
          sx={{ fontSize: mdClient ? "16px" : "20px", color: "#2E434D" }}
        >
          {t("help")}
        </Text>
      </Box>
      {isGetSupport && renderHelpdeskContact()}
    </Box>
  );

  // useEffect(() => {
  //   if (router.query.search) {
  //     handleSearch(`${router.query.search}`);
  //   }
  // }, []);

  return (
    <>
      <Box
        mt={isGetSupport ? 0 : 4}
        className={isGetSupport ? undefined : classes?.BackgroundStyle}
      >
        <Grid container alignItems="center" direction="column">
          <Grid item xs={12} width={isMD ? "100%" : "640px"}>
            {!isHaveTopic && (
              <FilterFaq
                filterProps={filterProps}
                filterList={filterData}
                router={router}
                search={search}
                isGetSupport={isGetSupport}
              />
            )}
          </Grid>
        </Grid>
        <Grid container alignItems="center" direction="column">
          <Grid item xs={12} width={isMD ? "100%" : "640px"}>
            <Box
              pb={isMD ? 2 : 3}
              sx={{
                ...(isMD && {
                  input: {
                    fontSize: 14,
                    fontFamily: fontFamSecondary,
                  },
                }),
              }}
            >
              {!isMobile && renderTopicTitle()}
              {!searchIconShown && isMD && (
                <SearchBox
                  onChange={handleSearch}
                  value={search}
                  placeholder={t("placeholder")}
                  onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) => {
                    if (evt.key === "Enter") {
                      handleSearch("");
                    }
                  }}
                  style={{
                    ...(isMD && {
                      marginBottom: "24px",
                      backgroundColor: "#FDFDFD",
                      border: "1px solid #DEE3ED",
                      height: "48px",
                    }),
                  }}
                />
              )}
              {renderListFaqItem()}
            </Box>
            {renderHelpdeskLayout(isGetSupport)}
          </Grid>
        </Grid>
      </Box>
      <DialogForm
        isOpen={isModalHelpdesk}
        setIsOpen={setIsModalHelpdesk}
        maxWidth="xs"
        className={isMobile ? classes.dialogWhatsapp : ""}
        image={
          <DynamicImage
            src={CustomerService}
            alt="img_contact_to_helpdesk"
            width={238}
            height={200}
          />
        }
        messages={[
          [
            isMobile ? (
              <Text
                variant="h3"
                weight="700"
                sx={{
                  fontSize: 16,
                  lineHeight: "28px",
                }}
              >
                {t("dialog.msg_title")}
              </Text>
            ) : (
              `${t("dialog.msg_title")}`
            ),
            "",
            "700",
            `${isMobile ? "" : "title"}`,
            `${isMobile ? "start" : "center"}`,
          ],
          [
            <Text variant={isMobile ? "body2" : "body1"}>{t("dialog.msg_desc")}</Text>,
            "light",
            `${isMobile ? "400" : "500"}`,
            "",
            `${isMobile ? "start" : "center"}`,
          ],
        ]}
      >
        <Button
          onClick={() => {
            window.open(
              `${apiWA}?phone=${helpdeskData?.phoneNumber}&text=${helpdeskData?.message}`,
              "_blank",
            );
            setIsModalHelpdesk(false);
          }}
          size="large"
          width="100%"
          variant="primary"
        >
          {t("dialog.submit")}
        </Button>
      </DialogForm>
    </>
  );
};

export default FAQ;
